import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEvent } from '../contexts/EventContext';

export const useAnalytics = () => {
  const { user, currentGroupSlug } = useAuth();
  const { selectedEvent } = useEvent();

  // Track page views
  const trackPageView = useCallback((page_title: string, page_location?: string) => {
    if (typeof globalThis.gtag === 'function') {
      globalThis.gtag('config', 'G-PGY3EPZFMW', {
        page_title,
        page_location: page_location || globalThis.location.href,
        // Add custom dimensions
        custom_map: {
          dimension1: currentGroupSlug || 'no-group',
          dimension2: selectedEvent?.name || 'no-event',
          dimension3: user ? 'authenticated' : 'anonymous',
        }
      });
    }
  }, [currentGroupSlug, selectedEvent, user]);

  // Track custom events
  const trackEvent = useCallback((action: string, category: string, label?: string, value?: number) => {
    if (typeof globalThis.gtag === 'function') {
      globalThis.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        // Add context
        group_slug: currentGroupSlug,
        event_name: selectedEvent?.name,
        user_id: user?.id?.toString(),
      });
    }
  }, [currentGroupSlug, selectedEvent, user]);

  // Predefined tracking functions for common actions
  const trackLogin = useCallback(() => {
    trackEvent('login', 'authentication');
  }, [trackEvent]);

  const trackLogout = useCallback(() => {
    trackEvent('logout', 'authentication');
  }, [trackEvent]);

  const trackGroupSwitch = useCallback((groupName: string) => {
    trackEvent('group_switch', 'navigation', groupName);
  }, [trackEvent]);

  const trackEventSwitch = useCallback((eventName: string) => {
    trackEvent('event_switch', 'navigation', eventName);
  }, [trackEvent]);

  const trackListCreation = useCallback((listType: string) => {
    trackEvent('list_create', 'lists', listType);
  }, [trackEvent]);

  const trackItemReservation = useCallback((itemName: string) => {
    trackEvent('item_reserve', 'reservations', itemName);
  }, [trackEvent]);

  const trackFeedbackSubmit = useCallback(() => {
    trackEvent('feedback_submit', 'engagement');
  }, [trackEvent]);

  return {
    trackPageView,
    trackEvent,
    trackLogin,
    trackLogout,
    trackGroupSwitch,
    trackEventSwitch,
    trackListCreation,
    trackItemReservation,
    trackFeedbackSubmit,
  };
};